import { Copy, Check } from 'lucide-react';
import { useState } from 'react';
import { getTextColorHex } from '../utils/color-contrast';

export default function StyleGuide() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Intro */}
      <section className="mb-12">
        <h1 className="mb-2 text-3xl font-bold" style={{ fontFamily: 'var(--font-primary)', color: '#3b0b86' }}>
          Ghid de Stil
        </h1>
        <p className="text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
          Sistemul de design pentru aplicația de evenimente pentru tineret Alba Iulia 2026
        </p>
      </section>

      {/* Colors */}
      <ColorSection />

      {/* Color Contrast Demo */}
      <ColorContrastSection />

      {/* Typography */}
      <TypographySection />

      {/* Spacing */}
      <SpacingSection />

      {/* Border Radius */}
      <BorderRadiusSection />

      {/* Shadows */}
      <ShadowsSection />

      {/* States */}
      <StatesSection />
    </div>
  );
}

function ColorSection() {
  const colors = [
    {
      name: 'Vivid Purple',
      description: 'Culoare primară, fundal, elemente dominante',
      hex: '#3b0b86',
      rgb: 'RGB(59, 11, 134)',
      cmyk: 'CMYK(56, 92, 0, 47)',
      variable: '--vivid-purple',
    },
    {
      name: 'Vivid Orange',
      description: 'Culoare accent, butoane de acțiune, elemente interactive',
      hex: '#ff6417',
      rgb: 'RGB(255, 100, 23)',
      cmyk: 'CMYK(0, 61, 91, 0)',
      variable: '--vivid-orange',
    },
    {
      name: 'Vivid Cyan',
      description: 'Culoare accent secundară, ilustrații, elemente de suport',
      hex: '#01ffff',
      rgb: 'RGB(1, 255, 255)',
      cmyk: 'CMYK(100, 0, 0, 0)',
      variable: '--vivid-cyan',
    },
    {
      name: 'Vivid Yellow-Green',
      description: 'Culoare accent, bare, evidențieri, elemente de subliniere',
      hex: '#e1f433',
      rgb: 'RGB(225, 244, 51)',
      cmyk: 'CMYK(8, 0, 79, 4)',
      variable: '--vivid-yellow-green',
    },
  ];

  return (
    <section className="mb-16">
      <h2 className="mb-6 text-2xl font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
        Culori Oficiale
      </h2>
      <p className="mb-6 text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
        Folosiți întotdeauna aceste valori exacte. Nu utilizați culori în afara acestei palete.
      </p>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {colors.map((color) => (
          <ColorCard key={color.hex} {...color} />
        ))}
      </div>
    </section>
  );
}

function ColorCard({ name, description, hex, rgb, cmyk, variable }: {
  name: string;
  description: string;
  hex: string;
  rgb: string;
  cmyk: string;
  variable: string;
}) {
  const [copied, setCopied] = useState('');

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(''), 2000);
  };

  return (
    <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
      <div className="h-32" style={{ backgroundColor: hex }} />
      <div className="p-4">
        <h3 className="mb-1 font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
          {name}
        </h3>
        <p className="mb-4 text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
          {description}
        </p>
        <div className="space-y-2">
          <CopyableValue
            label="HEX"
            value={hex}
            copied={copied === 'hex'}
            onCopy={() => copyToClipboard(hex, 'hex')}
          />
          <CopyableValue
            label="RGB"
            value={rgb}
            copied={copied === 'rgb'}
            onCopy={() => copyToClipboard(rgb, 'rgb')}
          />
          <CopyableValue
            label="CMYK"
            value={cmyk}
            copied={copied === 'cmyk'}
            onCopy={() => copyToClipboard(cmyk, 'cmyk')}
          />
          <CopyableValue
            label="CSS"
            value={`var(${variable})`}
            copied={copied === 'css'}
            onCopy={() => copyToClipboard(`var(${variable})`, 'css')}
          />
        </div>
      </div>
    </div>
  );
}

function CopyableValue({ label, value, copied, onCopy }: {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-md bg-muted px-2 py-1">
      <div>
        <span className="text-xs font-medium text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
          {label}
        </span>
        <span className="ml-2 text-xs font-mono">{value}</span>
      </div>
      <button
        onClick={onCopy}
        className="rounded p-1 hover:bg-background"
        aria-label={`Copy ${label}`}
      >
        {copied ? (
          <Check className="h-3 w-3 text-green-600" />
        ) : (
          <Copy className="h-3 w-3 text-muted-foreground" />
        )}
      </button>
    </div>
  );
}

function ColorContrastSection() {
  const colors = [
    {
      name: 'Vivid Purple',
      hex: '#3b0b86',
      variable: '--vivid-purple',
    },
    {
      name: 'Vivid Orange',
      hex: '#ff6417',
      variable: '--vivid-orange',
    },
    {
      name: 'Vivid Cyan',
      hex: '#01ffff',
      variable: '--vivid-cyan',
    },
    {
      name: 'Vivid Yellow-Green',
      hex: '#e1f433',
      variable: '--vivid-yellow-green',
    },
  ];

  return (
    <section className="mb-16">
      <h2 className="mb-6 text-2xl font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
        Contrast Culoare
      </h2>
      <p className="mb-6 text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
        Verificați contrastul culorilor pentru accesibilitate
      </p>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {colors.map((color) => (
          <ColorContrastCard key={color.hex} {...color} />
        ))}
      </div>
    </section>
  );
}

function ColorContrastCard({ name, hex, variable }: {
  name: string;
  hex: string;
  variable: string;
}) {
  const textColor = getTextColorHex(hex);

  return (
    <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
      <div
        className="flex h-32 items-center justify-center p-4"
        style={{ backgroundColor: hex }}
      >
        <p
          className="text-center text-2xl font-bold"
          style={{
            color: textColor,
            fontFamily: 'var(--font-primary)'
          }}
        >
          Alba Iulia<br />2026
        </p>
      </div>
      <div className="p-4">
        <h3 className="mb-1 font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
          {name}
        </h3>
        <p className="mb-4 text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
          Text automat: {textColor === '#ffffff' ? 'alb' : 'negru'}
        </p>
        <div className="rounded-md bg-muted px-3 py-2">
          <p className="text-xs font-medium text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
            Fundal
          </p>
          <code className="text-xs font-mono">{hex}</code>
        </div>
        <div className="mt-2 rounded-md bg-muted px-3 py-2">
          <p className="text-xs font-medium text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
            Text
          </p>
          <code className="text-xs font-mono">{textColor}</code>
        </div>
      </div>
    </div>
  );
}

function TypographySection() {
  return (
    <section className="mb-16">
      <h2 className="mb-6 text-2xl font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
        Tipografie
      </h2>
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Poppins */}
        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            Poppins – Font Primar
          </h3>
          <p className="mb-6 text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
            Pentru titluri, subtitluri și elemente de impact vizual
          </p>
          <div className="space-y-4">
            <div>
              <p className="mb-1 text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                Regular (400)
              </p>
              <p className="text-2xl" style={{ fontFamily: 'var(--font-primary)', fontWeight: 400 }}>
                Alba Iulia 2026
              </p>
            </div>
            <div>
              <p className="mb-1 text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                Medium (500)
              </p>
              <p className="text-2xl" style={{ fontFamily: 'var(--font-primary)', fontWeight: 500 }}>
                Alba Iulia 2026
              </p>
            </div>
            <div>
              <p className="mb-1 text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                SemiBold (600)
              </p>
              <p className="text-2xl" style={{ fontFamily: 'var(--font-primary)', fontWeight: 600 }}>
                Alba Iulia 2026
              </p>
            </div>
            <div>
              <p className="mb-1 text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                Bold (700)
              </p>
              <p className="text-2xl" style={{ fontFamily: 'var(--font-primary)', fontWeight: 700 }}>
                Alba Iulia 2026
              </p>
            </div>
          </div>
        </div>

        {/* Roboto Condensed */}
        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            Roboto Condensed – Font Secundar
          </h3>
          <p className="mb-6 text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
            Pentru text de corp, descrieri și informații secundare
          </p>
          <div className="space-y-4">
            <div>
              <p className="mb-1 text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                Regular (400)
              </p>
              <p className="text-lg" style={{ fontFamily: 'var(--font-secondary)', fontWeight: 400 }}>
                Capitala Tineretului din România
              </p>
            </div>
            <div>
              <p className="mb-1 text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                Italic (400)
              </p>
              <p className="text-lg" style={{ fontFamily: 'var(--font-secondary)', fontWeight: 400, fontStyle: 'italic' }}>
                Capitala Tineretului din România
              </p>
            </div>
            <div>
              <p className="mb-1 text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                Bold (700)
              </p>
              <p className="text-lg" style={{ fontFamily: 'var(--font-secondary)', fontWeight: 700 }}>
                Capitala Tineretului din România
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Type Scale */}
      <div className="mt-6 rounded-xl border bg-card p-6 shadow-sm">
        <h3 className="mb-4 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
          Scară Tipografică
        </h3>
        <div className="space-y-3">
          <TypeScaleItem size="4xl" pixels="36px" example="Titlu Principal" />
          <TypeScaleItem size="3xl" pixels="30px" example="Titlu Secțiune" />
          <TypeScaleItem size="2xl" pixels="24px" example="Titlu Subsecțiune" />
          <TypeScaleItem size="xl" pixels="20px" example="Titlu Card" />
          <TypeScaleItem size="lg" pixels="18px" example="Subtitlu" />
          <TypeScaleItem size="base" pixels="16px" example="Text Normal" />
          <TypeScaleItem size="sm" pixels="14px" example="Text Secundar" />
          <TypeScaleItem size="xs" pixels="12px" example="Etichetă Mică" />
        </div>
      </div>
    </section>
  );
}

function TypeScaleItem({ size, pixels, example }: { size: string; pixels: string; example: string }) {
  const sizeMap: { [key: string]: string } = {
    '4xl': 'var(--text-4xl)',
    '3xl': 'var(--text-3xl)',
    '2xl': 'var(--text-2xl)',
    'xl': 'var(--text-xl)',
    'lg': 'var(--text-lg)',
    'base': 'var(--text-base)',
    'sm': 'var(--text-sm)',
    'xs': 'var(--text-xs)',
  };

  return (
    <div className="flex items-baseline gap-4 border-b pb-3 last:border-0">
      <div className="min-w-[100px]">
        <span className="text-sm font-medium" style={{ fontFamily: 'var(--font-secondary)' }}>
          {size}
        </span>
        <span className="ml-2 text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
          {pixels}
        </span>
      </div>
      <p style={{ fontFamily: 'var(--font-primary)', fontSize: sizeMap[size] }}>
        {example}
      </p>
    </div>
  );
}

function SpacingSection() {
  const spacings = [
    { name: 'xs', value: '4px', variable: '--spacing-xs' },
    { name: 'sm', value: '8px', variable: '--spacing-sm' },
    { name: 'md', value: '16px', variable: '--spacing-md' },
    { name: 'lg', value: '24px', variable: '--spacing-lg' },
    { name: 'xl', value: '32px', variable: '--spacing-xl' },
    { name: '2xl', value: '48px', variable: '--spacing-2xl' },
  ];

  return (
    <section className="mb-16">
      <h2 className="mb-6 text-2xl font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
        Spațiere
      </h2>
      <p className="mb-6 text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
        Utilizați scara de spațiere pentru padding, margin și gap pentru a asigura consistența
      </p>
      <div className="rounded-xl border bg-card p-6 shadow-sm">
        <div className="space-y-4">
          {spacings.map((spacing) => (
            <div key={spacing.name} className="flex items-center gap-4">
              <div className="min-w-[80px]">
                <span className="text-sm font-medium" style={{ fontFamily: 'var(--font-secondary)' }}>
                  {spacing.name}
                </span>
                <span className="ml-2 text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                  {spacing.value}
                </span>
              </div>
              <div className="h-8 rounded" style={{ width: spacing.value, backgroundColor: '#3b0b86' }} />
              <code className="text-xs text-muted-foreground" style={{ fontFamily: 'monospace' }}>
                var({spacing.variable})
              </code>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BorderRadiusSection() {
  const radii = [
    { name: 'sm', value: '4px', variable: '--radius-sm' },
    { name: 'md', value: '6px', variable: '--radius-md' },
    { name: 'lg', value: '8px', variable: '--radius-lg' },
    { name: 'xl', value: '12px', variable: '--radius-xl' },
    { name: '2xl', value: '16px', variable: '--radius-2xl' },
  ];

  return (
    <section className="mb-16">
      <h2 className="mb-6 text-2xl font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
        Colțuri Rotunjite
      </h2>
      <p className="mb-6 text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
        Valorile de border-radius pentru elemente UI
      </p>
      <div className="rounded-xl border bg-card p-6 shadow-sm">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {radii.map((radius) => (
            <div key={radius.name} className="text-center">
              <div
                className="mx-auto mb-3 h-20 w-20 border-2"
                style={{ 
                  borderRadius: radius.value,
                  borderColor: '#3b0b86',
                  backgroundColor: '#e1f433'
                }}
              />
              <p className="text-sm font-medium" style={{ fontFamily: 'var(--font-secondary)' }}>
                {radius.name}
              </p>
              <p className="text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                {radius.value}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ShadowsSection() {
  const shadows = [
    { name: 'sm', description: 'Micro elevație', variable: '--shadow-sm' },
    { name: 'md', description: 'Elevație standard', variable: '--shadow-md' },
    { name: 'lg', description: 'Elevație ridicată', variable: '--shadow-lg' },
    { name: 'xl', description: 'Elevație maximă', variable: '--shadow-xl' },
  ];

  return (
    <section className="mb-16">
      <h2 className="mb-6 text-2xl font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
        Umbre / Elevație
      </h2>
      <p className="mb-6 text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
        Sistemul de umbre pentru crearea ierarhiei vizuale
      </p>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {shadows.map((shadow) => (
          <div key={shadow.name} className="rounded-xl border bg-card p-6">
            <div
              className="mb-4 h-20 rounded-lg bg-white"
              style={{ boxShadow: `var(${shadow.variable})` }}
            />
            <p className="text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
              {shadow.name}
            </p>
            <p className="text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
              {shadow.description}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function StatesSection() {
  return (
    <section className="mb-16">
      <h2 className="mb-6 text-2xl font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
        Stări Componente
      </h2>
      <p className="mb-6 text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
        Stările standard ale elementelor interactive
      </p>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            Butoane
          </h3>
          <div className="space-y-3">
            <StateExample label="Normal" />
            <StateExample label="Hover" hover />
            <StateExample label="Active" active />
            <StateExample label="Disabled" disabled />
          </div>
        </div>

        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            Input Fields
          </h3>
          <div className="space-y-3">
            <InputStateExample label="Normal" />
            <InputStateExample label="Focus" focus />
            <InputStateExample label="Error" error />
            <InputStateExample label="Disabled" disabled />
          </div>
        </div>
      </div>
    </section>
  );
}

function StateExample({ label, hover = false, active = false, disabled = false }: {
  label: string;
  hover?: boolean;
  active?: boolean;
  disabled?: boolean;
}) {
  let bgColor = '#3b0b86';
  let opacity = '1';
  
  if (hover) bgColor = '#4a0fa0';
  if (active) bgColor = '#2a0866';
  if (disabled) {
    bgColor = '#3b0b86';
    opacity = '0.5';
  }

  return (
    <div className="flex items-center gap-3">
      <button
        className="rounded-lg px-4 py-2 text-sm text-white transition-colors"
        style={{ 
          backgroundColor: bgColor,
          opacity,
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontFamily: 'var(--font-primary)'
        }}
        disabled={disabled}
      >
        Buton
      </button>
      <span className="text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
        {label}
      </span>
    </div>
  );
}

function InputStateExample({ label, focus = false, error = false, disabled = false }: {
  label: string;
  focus?: boolean;
  error?: boolean;
  disabled?: boolean;
}) {
  let borderColor = 'rgba(0, 0, 0, 0.1)';
  let ringColor = 'transparent';
  
  if (focus) {
    borderColor = '#3b0b86';
    ringColor = 'rgba(59, 11, 134, 0.2)';
  }
  if (error) {
    borderColor = '#d4183d';
    ringColor = 'rgba(212, 24, 61, 0.2)';
  }

  return (
    <div className="flex items-center gap-3">
      <input
        type="text"
        placeholder="Text"
        className="w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none transition-all"
        style={{ 
          borderColor,
          boxShadow: `0 0 0 3px ${ringColor}`,
          fontFamily: 'var(--font-secondary)',
          opacity: disabled ? '0.5' : '1',
          cursor: disabled ? 'not-allowed' : 'text'
        }}
        disabled={disabled}
      />
      <span className="min-w-[80px] text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
        {label}
      </span>
    </div>
  );
}